import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { QuoteList } from "../components/admin/QuoteList";
import { ProductForm } from "../components/admin/ProductForm";
import { CustomProductList } from "../components/admin/CustomProductList";
import { BrochureManager } from "../components/admin/BrochureManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "zalta_admin_token";

const TABS = [
  { id: "quotes", label: "Quote Requests" },
  { id: "add", label: "Add Product" },
  { id: "products", label: "All Products" },
  { id: "brochure", label: "Brochure" },
];

export const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("quotes");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(`Welcome, ${data.user.name}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed — check email and password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    toast.success("Logged out");
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#FAF8FC] flex items-center justify-center px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-[#221533]/10 rounded-2xl shadow-[0_25px_60px_-25px_rgba(142,90,158,0.4)] p-8"
        >
          <p className="font-clash text-3xl font-semibold text-[#74427F] text-center">
            ZALTA<sup className="text-[#E63946] text-sm">®</sup>
          </p>
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#221533]/40 text-center mt-2 mb-8">
            Admin Panel
          </p>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-3 bg-[#FAF8FC] border border-[#221533]/15 rounded-md px-4 py-3.5 text-sm focus:border-[#8E5A9E] transition-colors"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-5 bg-[#FAF8FC] border border-[#221533]/15 rounded-md px-4 py-3.5 text-sm focus:border-[#8E5A9E] transition-colors"
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#8E5A9E] hover:bg-[#74427F] disabled:opacity-50 text-white font-mono2 text-xs tracking-[0.25em] uppercase py-4 rounded-md transition-colors active:scale-[0.98]"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" data-testid="admin-back-home" className="block text-center mt-5 text-xs text-[#221533]/45 hover:text-[#74427F] transition-colors">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8FC]">
      <header className="bg-white border-b border-[#221533]/10 sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 h-[64px] flex items-center justify-between">
          <p className="font-clash text-xl sm:text-2xl font-semibold text-[#74427F]">
            ZALTA<sup className="text-[#E63946] text-[10px]">®</sup> <span className="text-[#221533]/40 font-normal text-base">Admin</span>
          </p>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-[#221533]/15 hover:border-[#E63946] hover:text-[#E63946] text-[#221533]/60 text-xs font-mono2 uppercase tracking-[0.15em] px-4 py-2.5 rounded-md transition-colors active:scale-95"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`font-mono2 text-[11px] tracking-[0.15em] uppercase px-5 py-3 rounded-xl border transition-all duration-300 active:scale-95 ${
                tab === t.id
                  ? "bg-[#8E5A9E] border-[#8E5A9E] text-white shadow-[0_10px_25px_-8px_rgba(142,90,158,0.6)]"
                  : "border-[#221533]/15 text-[#221533]/55 hover:bg-[#8E5A9E] hover:border-[#8E5A9E] hover:text-white hover:shadow-[0_10px_22px_-8px_rgba(142,90,158,0.5)] bg-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "quotes" && <QuoteList token={token} />}
        {tab === "add" && <ProductForm token={token} onCreated={() => { setRefreshKey((k) => k + 1); setTab("products"); }} />}
        {tab === "products" && <CustomProductList token={token} refreshKey={refreshKey} />}
        {tab === "brochure" && <BrochureManager token={token} />}
      </div>
    </div>
  );
};
